import React, { useState, useEffect } from 'react'
import { Container, Card, Spinner, Alert, Row, Col, Button } from 'react-bootstrap'
import d_log from 'loglevel'
import api_utils from '../lib/api_utils'

function UserInfo() {
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUserInfo = async () => {
    setLoading(true)
    setError(null)
    try {
      d_log.debug('Fetching user info from backend')
      const userInfoUrl = `${import.meta.env.VITE_SERVICE_URL}/userinfo`
      const headers = api_utils.form_auth_headers()
      const response = await api_utils.call_backend_url(userInfoUrl, headers)

      setUserInfo(response)
      d_log.debug('User info loaded successfully', response)
    } catch (err) {
      d_log.error('Failed to fetch user info:', err)
      setError(`Failed to load user information: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUserInfo()
  }, [])

  const handleReload = () => {
    d_log.debug('User requested reload of user info')
    fetchUserInfo()
  }

  const renderJsonAsKeyValue = (obj, title = "Data") => {
    if (!obj || typeof obj !== 'object') {
      return (
        <div className="text-muted">
          {obj ? String(obj) : 'No data available'}
        </div>
      )
    }

    return (
      <div>
        <h6 className="mb-3">{title}:</h6>
        {Object.entries(obj).map(([key, value]) => (
          <Row key={key} className="mb-2">
            <Col md={3}>
              <strong>{key}:</strong>
            </Col>
            <Col md={9}>
              {typeof value === 'object' && value !== null ? (
                <pre className="bg-light p-2 rounded small">
                  {JSON.stringify(value, null, 2)}
                </pre>
              ) : (
                <code>{typeof value === 'string' ? `"${value}"` : String(value)}</code>
              )}
            </Col>
          </Row>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading user information...</span>
        </Spinner>
        <p className="mt-3">Loading user information...</p>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          <Alert.Heading>Error Loading User Information</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>User Information</h2>
          <p className="text-muted mb-0">Information retrieved from the backend service</p>
        </div>
        <Button
          variant="outline-primary"
          onClick={handleReload}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Reloading...
            </>
          ) : (
            <>
              🔄 Reload
            </>
          )}
        </Button>
      </div>

      <Card className="mt-4">
        <Card.Header>
          <h5>Backend User Data</h5>
        </Card.Header>
        <Card.Body>
          {userInfo ? (
            <div>
              {renderJsonAsKeyValue(userInfo, "User Information")}

              <hr className="my-4" />

              <h6>Raw JSON Response:</h6>
              <pre className="bg-light p-3 rounded">
                {JSON.stringify(userInfo, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-center text-muted">
              <p>No user information available from the backend.</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default UserInfo